import { useState } from "react";
import { Flex, Typography, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { UsersIcon, PlusIcon } from "@phosphor-icons/react";
import { AlertCard, Button, Card, ProgressBar, Skeleton } from "@libertify/ui";
import styled from "styled-components";
import { useQuota, useGetActiveSubscriptions } from "#src/hooks";
import { QuotaFeatures } from "@/constants";
import { AddSeatsModal } from "./AddSeatsModal";

const { Text, Title } = Typography;

const StyledCard = styled(Card)`
  margin-bottom: 24px;
`;

export const SeatsQuotaCard = ({
  onSeatsUpdated,
}: {
  onSeatsUpdated?: () => void;
}) => {
  const { token } = theme.useToken();
  const t = useTranslate();
  const { getQuota, isLoading, isNearLimit, isBlocked, refetch } = useQuota();
  const { subscriptions } = useGetActiveSubscriptions();
  const [isAddSeatsModalOpen, setIsAddSeatsModalOpen] = useState(false);

  const seatQuota = getQuota(QuotaFeatures.UserLicenseAssignment);

  // Check if user is on a paid plan (not free or trial)
  const isPaidPlan =
    subscriptions?.license_type !== "Free" &&
    subscriptions?.license_type !== "Trial";
  const currentSeats = seatQuota?.quota_limit ?? 0;
  const usedSeats = seatQuota?.usage ?? 0;
  const remainingSeats = Math.max(0, currentSeats - usedSeats);
  const usagePercent = currentSeats > 0 ? (usedSeats / currentSeats) * 100 : 0;

  const isAtLimit = isBlocked(QuotaFeatures.UserLicenseAssignment);
  const isNearingLimit = isNearLimit(QuotaFeatures.UserLicenseAssignment, 0.8);

  const getProgressColor = () => {
    if (isAtLimit) return token.colorError;
    if (isNearingLimit) return token.colorWarning;
    return token.colorSuccess;
  };

  const handleAddSeatsSuccess = async () => {
    setIsAddSeatsModalOpen(false);
    await refetch();
    onSeatsUpdated?.();
  };

  if (isLoading || !seatQuota) {
    return (
      <StyledCard padding="large">
        <Flex justify="space-between" align="flex-start">
          <Flex gap={16} align="flex-start">
            <Skeleton variant="circular" width={48} height={48} />
            <Flex vertical gap={8}>
              <Skeleton variant="rectangular" width={120} height={20} />
              <Skeleton variant="rectangular" width={200} height={16} />
            </Flex>
          </Flex>
          <Skeleton variant="rectangular" width={100} height={36} />
        </Flex>
        <Flex vertical gap={8} style={{ marginTop: 24 }}>
          <Flex justify="space-between">
            <Skeleton variant="rectangular" width={80} height={16} />
            <Skeleton variant="rectangular" width={60} height={16} />
          </Flex>
          <Skeleton variant="rectangular" width="100%" height={8} />
        </Flex>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard padding="large">
        <Flex justify="space-between" align="flex-start">
          <Flex gap={16} align="flex-start">
            <Flex
              align="center"
              justify="center"
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: "#e6f7ff",
              }}
            >
              <UsersIcon size={24} weight="duotone" color="#1890ff" />
            </Flex>
            <Flex vertical>
              <Title level={5} style={{ margin: 0 }}>
                {t("seatsQuota.title")}
              </Title>
              <Text type="secondary" style={{ marginTop: 4 }}>
                {t("seatsQuota.description")}
              </Text>
            </Flex>
          </Flex>

          {isPaidPlan && (
            <Button
              variant="primary"
              leftIcon={<PlusIcon />}
              onClick={() => setIsAddSeatsModalOpen(true)}
            >
              {t("seatsQuota.addSeats")}
            </Button>
          )}
        </Flex>

        <Flex vertical gap={8} style={{ marginTop: 24 }}>
          <Flex justify="space-between" align="center">
            <Text>
              {t("seatsQuota.usage", { used: usedSeats, total: currentSeats })}
            </Text>
            <Text
              type={
                isAtLimit ? "danger" : isNearingLimit ? "warning" : "secondary"
              }
            >
              {t("seatsQuota.remaining", { count: remainingSeats })}
            </Text>
          </Flex>
          <ProgressBar
            percent={usagePercent}
            showInfo={false}
            color={getProgressColor()}
            trackColor="#f0f0f0"
            height={8}
          />
        </Flex>

        {isAtLimit && (
          <AlertCard
            type="error"
            title={t(
              isPaidPlan
                ? "seatsQuota.atLimitMessage"
                : "seatsQuota.atLimitMessageFreePlan"
            )}
          />
        )}

        {isNearingLimit && !isAtLimit && (
          <AlertCard
            type="warning"
            title={t(
              isPaidPlan
                ? "seatsQuota.nearLimitMessage"
                : "seatsQuota.nearLimitMessageFreePlan"
            )}
          />
        )}
      </StyledCard>

      <AddSeatsModal
        open={isAddSeatsModalOpen}
        onCancel={() => setIsAddSeatsModalOpen(false)}
        onSuccess={handleAddSeatsSuccess}
        seatsNeeded={1}
      />
    </>
  );
};
